import heic2any from 'heic2any';
import { debug } from './debug';

/**
 * Converts a HEIC/HEIF file to JPEG format
 * @param file - The HEIC/HEIF file to convert
 * @returns A new File object in JPEG format
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    debug.log(`[convertHeicToJpeg] Starting conversion for: ${file.name}`);
    
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    });

    // heic2any can return Blob or Blob[], handle both cases
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    // Create new filename with .jpg extension
    const newFilename = file.name.replace(/\.(heic|heif)$/i, '.jpg');

    // Create new File object
    const convertedFile = new File([blob], newFilename, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });

    debug.log(`[convertHeicToJpeg] Conversion successful: ${newFilename}`);
    return convertedFile;
  } catch (error) {
    console.error('[convertHeicToJpeg] Conversion failed:', error);
    throw new Error(`Failed to convert HEIC image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Converts an SVG file to PNG data URL for CLIP embedding generation
 * @param url - The URL of the SVG file
 * @param maxDimension - Maximum width/height for the PNG (default 1024)
 * @returns PNG data URL (base64 encoded)
 */
export async function convertSvgToPng(url: string, maxDimension: number = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Calculate dimensions maintaining aspect ratio
        let width = img.width || img.naturalWidth;
        let height = img.height || img.naturalHeight;

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Create canvas and render SVG
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw white background (SVGs can be transparent)
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        // Draw the SVG
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to PNG data URL
        const dataUrl = canvas.toDataURL('image/png', 0.9);
        debug.log(`[convertSvgToPng] Conversion successful: ${width}x${height}px`);
        resolve(dataUrl);
      } catch (error) {
        reject(new Error(`Failed to render SVG: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load SVG image'));
    };

    img.src = url;
  });
}
